import { useApiQuery, useApiMutation, useApiDelete } from "lib/react-query";
import customFetch from "services/api";

// Plain async functions for auth flows (no hooks needed)
export const loginAdmin = (body) =>
  customFetch("dashboard/login", { method: "POST", body });

export const forgetPassword = (body) =>
  customFetch("admins/forget-password", { method: "POST", body });

export const checkOtp = (body) =>
  customFetch("admins/check-otp", {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

export const resetPassword = (body) =>
  customFetch("admins/change-forget-password", {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

export const validateAdmin = (body) =>
  customFetch("dashboard/validate-admin", { method: "POST", body });

/**
 * The signed-in user, fetched imperatively. The support-access handoff needs the permissions
 * in storage before the app boots, which is one step earlier than a React Query hook can run.
 */
export const fetchProfile = () => customFetch("dashboard/profile");

// React Query hooks
export const useAdminProfile = () => {
  return useApiQuery("dashboard/profile");
};

export const useAdmins = () => {
  return useApiQuery("dashboard/admins", null, true);
};

export const adminProfileUrl = (id) => `dashboard/admins/${id}/profile`;

export const useAdmin = (id) => {
  return useApiQuery(id ? adminProfileUrl(id) : null);
};

/** A 404 on an admin profile page redirects to "not found"; toasting it as well is noise. */
const ADMIN_RECORD_FETCH = { suppressNotFoundToast: true };

/** The admin profile page's own record query — the same endpoint as `useAdmin`. */
export const useAdminDetails = (id) =>
  useApiQuery(
    id ? adminProfileUrl(id) : null,
    null,
    false,
    null,
    ADMIN_RECORD_FETCH
  );

export const useRoles = () => {
  return useApiQuery("dashboard/roles");
};

export const useUserPositions = () => {
  return useApiQuery("dashboard/users-positions");
};

export const useAdminMutation = (callbacks = {}) => {
  return useApiMutation(callbacks);
};

export const adminLogoutAllDevicesUrl = (id) =>
  `dashboard/admins/${id}/logout-all-devices`;
export const adminDeleteUrl = (id) => `dashboard/admins/${id}/delete`;
export const adminRestoreUrl = (id) => `dashboard/admins/${id}/restore`;

export const useAdminDelete = (setData) => {
  return useApiDelete("dashboard/admins", setData);
};

/* ── Another admin's account security ─────────────────────────────────────
 *
 * Read-only views of someone else's second factors, devices and login history, for
 * their profile page; gated by `admins.edit`, like signing that account out, so the page
 * fetches them only for a user who holds it. The URLs sit under `dashboard/admins`, so
 * logging the account out of every device refreshes them.
 *
 * A 404 here means the account is gone, which the page's own record query already
 * answers with a redirect — so these do not toast it a second time.
 */

export const adminTwoFactorUrl = (id) => `dashboard/admins/${id}/two-factor`;
export const adminLoginSessionsUrl = (id) => `dashboard/admins/${id}/sessions`;
export const adminLoginHistoryUrl = (id) =>
  `dashboard/admins/${id}/login-history`;

export const useAdminTwoFactorMethods = (id) =>
  useApiQuery(
    id ? adminTwoFactorUrl(id) : null,
    null,
    false,
    null,
    ADMIN_RECORD_FETCH
  );

// Keeps the current page on screen while the next one loads, as `useLoginSessions` does.
export const useAdminLoginSessions = (id, page) =>
  useApiQuery(
    id ? adminLoginSessionsUrl(id) : null,
    page,
    false,
    null,
    ADMIN_RECORD_FETCH,
    { placeholderData: (prev) => prev }
  );

export const useAdminLoginHistory = (id) =>
  useApiQuery(
    id ? adminLoginHistoryUrl(id) : null,
    null,
    false,
    null,
    ADMIN_RECORD_FETCH
  );

/* ── Account security ──────────────────────────────────────────────────────
 *
 * The signed-in user's own second factors, devices and login history. Every one
 * of these endpoints reads the user off the token and takes no id, which is why
 * none of them is permission-gated and none of them accepts one.
 */

export const SECURITY_BASE = "dashboard/profile/security";

export const twoFactorUrl = `${SECURITY_BASE}/two-factor`;
export const twoFactorStartUrl = `${SECURITY_BASE}/two-factor/start`;
export const twoFactorConfirmUrl = `${SECURITY_BASE}/two-factor/confirm`;
export const twoFactorDisableUrl = `${SECURITY_BASE}/two-factor/disable`;
export const twoFactorPrimaryUrl = `${SECURITY_BASE}/two-factor/primary`;
export const loginSessionsUrl = `${SECURITY_BASE}/sessions`;
export const loginSessionUrl = (id) => `${SECURITY_BASE}/sessions/${id}`;
export const revokeOtherSessionsUrl = `${SECURITY_BASE}/sessions/revoke-others`;
export const loginHistoryUrl = `${SECURITY_BASE}/login-history`;
export const passkeysUrl = `${SECURITY_BASE}/passkeys`;
export const passkeyOptionsUrl = `${SECURITY_BASE}/passkeys/options`;
export const passkeyUrl = (id) => `${SECURITY_BASE}/passkeys/${id}`;
export const changePasswordUrl = "dashboard/admins/change-password";
export const updateProfileUrl = "dashboard/update-profile";

/** The 2FA card's whole payload: every supported method, enrolled or not, plus the policy. */
export const useTwoFactorMethods = () => useApiQuery(twoFactorUrl);

// Keeps the current page on screen while the next one loads, instead of flashing the empty state.
export const useLoginSessions = (page) =>
  useApiQuery(
    loginSessionsUrl,
    page,
    false,
    null,
    {},
    { placeholderData: (prev) => prev }
  );

export const useLoginHistory = () => useApiQuery(loginHistoryUrl);

/** The devices a user has registered a passkey on. Never returns key material. */
export const usePasskeys = () => useApiQuery(passkeysUrl);

/**
 * The security actions are plain functions rather than `useApiMutation` calls, because
 * every one of them needs its own answer to "what happens when this fails".
 *
 * `useApiMutation` swallows the result: it fires a success toast when the call worked and
 * lets the global handler toast an error when it did not, and the caller cannot tell which
 * happened. That is right for a list page — the row either changed or it did not — and
 * wrong for a dialog, where a rejected code has to leave the dialog open with the input
 * focused rather than closing as though it had worked.
 *
 * So the three dialog-driven calls below pass `returnFullErrorResponse` and hand the
 * rejection back to the caller; the rest let the global error toast do its job. Callers
 * refresh the page's queries with `invalidateQueries(queryClient, "dashboard/profile")`,
 * which matches all three security lists and the profile itself.
 */

/** Sends the SMS, or mints the authenticator secret and its QR. Nothing is enabled yet. */
export const startTwoFactorEnrolment = (body) =>
  customFetch(twoFactorStartUrl, {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

/** The only call that turns a method on: the user has to echo a code back first. */
export const confirmTwoFactorEnrolment = (body) =>
  customFetch(twoFactorConfirmUrl, {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

export const disableTwoFactorMethod = (body) =>
  customFetch(twoFactorDisableUrl, { method: "POST", body });

export const setPrimaryTwoFactorMethod = (body) =>
  customFetch(twoFactorPrimaryUrl, { method: "POST", body });

export const revokeLoginSession = (id) =>
  customFetch(loginSessionUrl(id), { method: "DELETE" });

export const revokeOtherLoginSessions = () =>
  customFetch(revokeOtherSessionsUrl, { method: "POST", body: {} });

/* ── Passkeys ──────────────────────────────────────────────────────────────
 *
 * Registration is two calls because WebAuthn is: the server issues a challenge,
 * the browser has the authenticator sign it, the server verifies the signature.
 * The `ceremony_token` from the first call names the challenge the second is
 * checked against — the browser never chooses it, which is the point.
 */

export const startPasskeyRegistration = () =>
  customFetch(passkeyOptionsUrl, {
    method: "POST",
    body: {},
    returnFullErrorResponse: true,
  });

export const finishPasskeyRegistration = (body) =>
  customFetch(passkeysUrl, {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

export const deletePasskey = (id) =>
  customFetch(passkeyUrl(id), { method: "DELETE" });

/** Answers a pending sign-in challenge with an assertion instead of a typed code. */
export const verifyPasskeyChallenge = (body) =>
  customFetch("dashboard/two-factor/passkey/verify", {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

export const changeOwnPassword = (body) =>
  customFetch(changePasswordUrl, {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

/* ── Login two-factor challenge ────────────────────────────────────────────
 *
 * Plain functions rather than hooks: these run on the login screen, before there
 * is a token or a React Query cache to speak of. `returnFullErrorResponse` keeps
 * the rejection in hand so the code step can show "wrong code" against the input
 * instead of the global error toast, which on a login screen reads as a crash.
 */

export const verifyTwoFactorChallenge = (body) =>
  customFetch("dashboard/two-factor/verify", {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });

export const resendTwoFactorChallenge = (body) =>
  customFetch("dashboard/two-factor/resend", {
    method: "POST",
    body,
    returnFullErrorResponse: true,
  });
